import {Pressable, View} from 'react-native'
import {plural, selectOrdinal} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {
  type DocumentLeaderboardEntry,
  type DocumentPlayerProof,
  documentStripLead,
  LEADERBOARD_TOP,
} from '#/lib/document/report'
import {atoms as a} from '#/alf'
import {AvatarStack} from '#/components/AvatarStack'
import {Text} from '#/components/Typography'

/**
 * The film the pill wears on the *paper*.
 *
 * On the card the strip sits on the page itself — a white margin, a figure, a
 * heading — and only a dark scrim reads on every page, which is also why it is
 * the same black the stage's own controls wear. That film does not follow the
 * app's mode, because the page under it does not: a PDF page is white paper in
 * the dark mode too.
 */
const PAGE_FILM = 'rgba(0, 0, 0, 0.55)'

/**
 * Who has played a document, on the document's own page.
 *
 * A document that asks something is a thing other people are working through, and
 * the card should say so: a reader who sees that their class has already been
 * through it has a reason to start, and one who is told nothing has only the
 * promise of the questions. So the strip is social proof with a press behind it —
 * the faces are the evidence, the line beside them is the encouragement, and the
 * whole thing opens the leaderboard.
 *
 * It sits *on the paper*, in the band the page's caption used to hold, because
 * that is what it is: a line about this document, drawn where the reader is
 * looking, and not a row of the card's furniture under the title. Inside the pill
 * it keeps the shape a caption takes — small faces, one short line — so it reads
 * as part of the page rather than as a panel over it.
 *
 * The card is the only surface that draws it. The full screen reader used to
 * carry a copy in its own chrome, and does not any more: a reader who pressed
 * Play is reading a document, and everything in there belongs to the page in
 * front of them. Who has played is a thing said *to* somebody deciding whether to
 * start, so it stays where that decision is made.
 *
 * Three things make the line worth reading rather than generic:
 *
 *  - **The faces are the people the reader knows**, when there are any. A stranger's
 *    avatar says "somebody played"; a friend's says "you are behind".
 *  - **A friend on the podium wears the crown** (`AvatarStack`, gold ring and all),
 *    and the line names their place, because that is the one sentence on the card
 *    that can make a reader press Play.
 *  - **The numbers are the real ones.** Every figure here comes from the plays the
 *    server listed, so a document with one play says one — a card that invented a
 *    crowd would be worth ignoring.
 *  - **The reader's own standing is drawn as its own line.** A strip that tells
 *    somebody already on the board to "see where you land" is answering a
 *    question they are not asking, and a friend is only named while they are
 *    still ahead of them — see `documentStripLead`. So when the line above
 *    speaks of somebody else (a crowned friend, the crowd), the reader's place
 *    goes under it on a line of its own, a size down, rather than riding along
 *    on the end of the sentence: the podium line is what makes them want the
 *    place, and their own standing is the answer they will look for second.
 *    When the line is *about* them the two would say the same thing, so it stays
 *    a single line.
 */
export function DocumentPlayerStrip({
  proof,
  onOpen,
}: {
  proof: DocumentPlayerProof
  onOpen: () => void
}) {
  const {t: l} = useLingui()
  const film = PAGE_FILM
  const ink = '#fff'
  const {players, crowned, known, others, readers, viewer} = proof

  const nameOf = (entry: DocumentLeaderboardEntry) =>
    entry.profile.displayName?.trim() || entry.handle || ''
  const named = known.filter(entry => !!nameOf(entry))
  const first = named[0]
  const second = named[1]
  /** Who the line is about: a friend still ahead, the reader, or the others. */
  const lead = documentStripLead(proof)

  const othersText = plural(others, {one: '# other', other: '# others'})

  let caption: React.ReactNode
  if (lead.kind === 'name') {
    const name = nameOf(lead.entry)
    caption =
      lead.entry.rank === 1
        ? l`${name} is #1 on its questions — think you can take the crown?`
        : l`${name} is ${placeOf(lead.entry.rank)} on its questions — can you catch up?`
  } else if (lead.kind === 'you') {
    const place = placeOf(lead.entry.rank)
    caption =
      lead.entry.rank === 1
        ? readers === 1
          ? l`You're the first to take its questions — think anyone can catch you?`
          : l`You're ${place} of ${readers} on its questions — think anyone can catch you?`
        : lead.entry.rank <= LEADERBOARD_TOP
          ? l`You're ${place} of ${readers} on its questions — can you take the crown?`
          : l`You're ${place} of ${readers} on its questions — can you climb to the podium?`
  } else if (first && second) {
    caption =
      others > 0
        ? l`${nameOf(first)}, ${nameOf(second)} and ${othersText} have played its questions`
        : l`${nameOf(first)} and ${nameOf(second)} have played its questions`
  } else if (first) {
    caption =
      others > 0
        ? l`${nameOf(first)} and ${othersText} have played its questions`
        : l`${nameOf(first)} has played its questions`
  } else if (readers === 1) {
    caption = l`One reader has played its questions — can you beat them?`
  } else {
    caption = l`${readers} readers have played its questions — see where you land`
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={l`Who has played this document`}
      accessibilityHint={l`Opens the leaderboard`}
      onPress={onOpen}
      testID="documentPlayerStrip"
      style={({pressed}) => [
        a.flex_row,
        a.align_center,
        {
          alignSelf: 'flex-start',
          maxWidth: '100%',
          gap: 8,
          paddingLeft: 8,
          paddingRight: 12,
          paddingVertical: 7,
          borderRadius: 10,
          backgroundColor: film,
          opacity: pressed ? 0.8 : 1,
          cursor: 'pointer',
        },
      ]}>
      <AvatarStack
        size={20}
        profiles={players.map(entry => entry.profile)}
        crownDids={crowned.map(entry => entry.playerDid)}
        backgroundColor={film}
      />
      <View style={[a.flex_shrink, {minWidth: 0}]}>
        <Text style={[a.text_xs, {color: ink}]} numberOfLines={2}>
          {caption}
        </Text>
        {viewer && lead.kind !== 'you' && (
          /*
           * White, not a dimmed white: the pill's film is a mid grey on the
           * card, and 80% white on it is about 3.6:1 — under what small text
           * may be. The size step is what makes this line subordinate; the
           * contrast stays with the line above it.
           */
          <Text style={[a.text_2xs, {color: ink}]} numberOfLines={1}>
            {l`You're ${placeOf(viewer.rank)} of ${readers}`}
          </Text>
        )}
      </View>
    </Pressable>
  )
}

/**
 * A place, as English says it: 1st, 2nd, 3rd.
 *
 * An ordinal rather than a bare number because the sentence it lands in is about
 * winning — *"is 2nd on its questions"* is a standing, *"is 2"* is a field in a
 * table.
 */
function placeOf(rank: number): string {
  return selectOrdinal(rank, {
    one: '#st',
    two: '#nd',
    few: '#rd',
    other: '#th',
  })
}
